import { Flex, Input, Text, Transition } from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { Dialog } from 'src/design_system/molecules/dialog/Dialog'
import { sanitizeUsername } from 'src/helpers/sanitizeUsername'
import {
  useActions,
  useDebounce,
  useEmailValidation,
  useSelector,
  useState,
} from 'src/hooks'
import { useSocPublicImageBucketUrl } from 'src/hooks/useSocPublicImageBucketUrl'
import useUsernameValidation from 'src/hooks/useUsernameValidation'
import { useUpdateAccount } from 'src/react_query'
import { SponsorSearchTile } from './SponsorSearchTile/SponsorSearchTile'

type ExclusiveOfferDialogProps = {
  onFinishedJoin?: () => void
  onClose?: () => void
}

export const ExclusiveOfferDialog = (props: ExclusiveOfferDialogProps) => {
  const { onFinishedJoin, onClose } = props
  const updateAccountMutation = useUpdateAccount()
  const actions = useActions()
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [sponsorGenId, setSponsorGenId] = useState<string | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const [updateAccountFailureMessage, setUpdateAccountFailureMessage] =
    useState<string>()
  const isMobile = useSelector(state => state.window.width <= 754)

  const debouncedEmail = useDebounce(email, 500)
  const emailValidation = useEmailValidation(debouncedEmail)

  const handleUsernameChange = (value: string) => {
    setUsername(sanitizeUsername(value))
  }
  const debouncedUsername = useDebounce(username, 500)
  const usernameValidation = useUsernameValidation(debouncedUsername)

  const premiumCardCoversGridImageUrl = useSocPublicImageBucketUrl(
    'PremiumCardCoversGrid.png',
  )
  const digitalAndPrintedSendsImageUrl = useSocPublicImageBucketUrl(
    'digitalAndPrintedSends.png',
  )

  const createButtonStyles = {
    display: 'flex',
    flexDirection: 'row' as const,
    width: '100%',
    background: 'rgb(40, 40, 40)',
    padding: '10px',
    border: 'none',
    borderRadius: '.8rem',
    marginTop: '10px',
    alignitems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'Montserrat, sans-serif',
    color: '#fff',
    fontWeight: 600,
  }

  const onSubmit = async () => {
    try {
      setIsLoading(true)
      if (
        !usernameValidation.isValid ||
        usernameValidation.isLoading ||
        !emailValidation.isValid ||
        emailValidation.isLoading
      ) {
        return
      }
      const {
        updateAccount: { account: updatedAccount },
      } = await updateAccountMutation.mutateAsync({
        account: {
          username,
          password,
          email,
          sponsorGenealogyId: sponsorGenId,
        },
        joinReward: '3daytrial',
      })
      actions.updatedAccount(updatedAccount)
      actions.setShouldDisplayJoinConfirmation(true)
      onFinishedJoin?.()
    } catch (error) {
      setUpdateAccountFailureMessage(
        error?.toString() ?? 'Failed to update account.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return <Transition title={'Creating your Account'} isLoading={isLoading} />
  }
  return (
    <Dialog
      isOpen={true}
      onClose={() => onClose?.()}
      padding="0"
      sheetBgColorOverride="linear-gradient(to right, #009C89, #98FDDF)"
      title="Set Up Your Account"
      titleColor="#fff"
      overflow="hidden"
      maxWidth="840px"
      mobileBreakPointOverride={754}
    >
      <div
        style={{
          position: 'absolute',
          height: '50%',
          width: '100%',
          backgroundImage: `url(${premiumCardCoversGridImageUrl})`,
          backgroundRepeat: 'repeat',
          backgroundSize: 'contain',
          opacity: '.4',
          transform: 'scale(2) translate(-10.7%, -12%)',
          zIndex: -1,
          transformOrigin: 'center center',
        }}
      />
      <Flex
        flexDirection={isMobile ? 'column' : 'row'}
        columnGap={'x2'}
        rowGap={'x2'}
        height={isMobile ? '100%' : 'unset'}
        inset="36px 24px 36px 24px"
        zIndex={1}
        style={{
          background:
            'linear-gradient(to bottom, #ececec00, #ececec00, #ececec, #ececec, #ececec, #ececec)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
            rowGap: '1.25rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <img
              src={digitalAndPrintedSendsImageUrl}
              alt="GoodVibesCardFrontPanel"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '100%',
                height: '18rem',
                objectFit: 'cover',
              }}
            />
          </div>
          <Flex flexDirection="column" rowGap="x1" alignItems="flex-start">
            <Text
              type={'footnote'}
              weight="semiBold"
              content={'Sponsor'}
              style={{ marginLeft: '4px' }}
            />
            <SponsorSearchTile
              isAffiliateJoining={false}
              onSelectSponsor={genId => setSponsorGenId(genId)}
              selectedGenId={sponsorGenId}
            />
          </Flex>
        </div>
        <Flex
          flexDirection="column"
          width={isMobile ? '100%' : '50%'}
          rowGap="x2_5"
        >
          <Text
            type="caption"
            alignment="center"
            weight="semiBold"
            style={{ fontSize: '18px', color: '404040' }}
          >
            Finish setting up your account to continue sending more ACTs of
            kindness to those that need to hear from you.
          </Text>
          <Input
            icon="envelope"
            placeholder="Email"
            onChange={setEmail}
            value={email}
            type={'text'}
            message={
              emailValidation.errorMessage && email
                ? {
                    type: 'danger',
                    content: emailValidation.errorMessage,
                    icon: 'information',
                  }
                : undefined
            }
            isFullWidth={true}
            isLoading={emailValidation.isLoading}
          />
          <Input
            icon="user"
            placeholder="Username"
            onChange={handleUsernameChange}
            value={username}
            type={'text'}
            message={
              !usernameValidation.isValid &&
              !!username &&
              !usernameValidation.isLoading
                ? {
                    type: 'danger',
                    content: 'Username is already taken.',
                  }
                : undefined
            }
            isFullWidth={true}
            isLoading={usernameValidation.isLoading}
          />
          <Input
            icon="lock"
            placeholder="Password"
            type="password"
            onChange={setPassword}
            value={password}
            message={
              updateAccountFailureMessage
                ? { type: 'danger', content: updateAccountFailureMessage }
                : undefined
            }
            isFullWidth={true}
          />
          <div style={{ ...createButtonStyles }} onClick={onSubmit}>
            Create Account
          </div>
        </Flex>
      </Flex>
    </Dialog>
  )
}
